'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import Icon from '@/components/ui/AppIcon';
import { supabaseFetch } from '@/lib/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';

interface Category {
  id: string;
  name: string;
  nameEn: string;
  icon: string;
  svgLogo?: string;
  description: string;
  descriptionEn: string;
  partnerCount: number;
  color: string;
}

const CategoriesSection = () => {
  const { t } = useLanguage();
  const [categories, setCategories] = useState<Category[]>([
    {
      id: '1',
      name: 'الطب والصحة',
      nameEn: 'Medical & Health',
      icon: 'HeartIcon',
      description: 'مراكز طبية وعيادات متخصصة',
      descriptionEn: 'Medical centers and specialized clinics',
      partnerCount: 0,
      color: 'from-red-500 to-pink-500'
    },
    {
      id: '2',
      name: 'المطاعم والمقاهي',
      nameEn: 'Restaurants & Cafes',
      icon: 'BuildingStorefrontIcon',
      description: 'أشهر المطاعم والمقاهي',
      descriptionEn: 'Top restaurants and cafes',
      partnerCount: 0,
      color: 'from-orange-500 to-yellow-500'
    },
    {
      id: '3',
      name: 'الملابس والأزياء',
      nameEn: 'Clothing & Fashion',
      icon: 'ShoppingBagIcon',
      description: 'محلات الأزياء والإكسسوارات',
      descriptionEn: 'Fashion stores and accessories',
      partnerCount: 0,
      color: 'from-purple-500 to-pink-500'
    },
    {
      id: '4',
      name: 'الحلويات والمخبوزات',
      nameEn: 'Sweets & Bakeries',
      icon: 'CakeIcon',
      description: 'محلات الحلويات والمخابز',
      descriptionEn: 'Sweet shops and bakeries',
      partnerCount: 0,
      color: 'from-pink-500 to-rose-500'
    },
    {
      id: '5',
      name: 'سيارات',
      nameEn: 'Automotive',
      icon: 'WrenchScrewdriverIcon',
      svgLogo: '/assets/car-maintenance-category.svg',
      description: 'مراكز صيانة وخدمات السيارات',
      descriptionEn: 'Car maintenance and automotive services',
      partnerCount: 0,
      color: 'from-blue-600 to-indigo-600'
    },
    {
      id: '6',
      name: 'أخرى',
      nameEn: 'Others',
      icon: 'EllipsisHorizontalCircleIcon',
      description: 'فئات وخدمات متنوعة أخرى',
      descriptionEn: 'Various other categories and services',
      partnerCount: 0,
      color: 'from-teal-500 to-cyan-500'
    }
  ]);

  useEffect(() => {
    const fetchPartnerCounts = async () => {
      const { data: dbCategories, error } = await supabaseFetch('categories', {}, 'id,name');

      if (error || !dbCategories) {
        console.error('Error fetching categories:', error);
        return;
      }

      const countsPromises = dbCategories.map(async (dbCat: any) => {
        const { data: partners } = await supabaseFetch(
          'partners',
          { category_id: `eq.${dbCat.id}`, select: 'id' },
          'id'
        );
        return { name: dbCat.name.trim(), count: partners?.length || 0 };
      });

      const counts = await Promise.all(countsPromises);

      setCategories(prevCategories =>
        prevCategories.map(cat => {
          const normalizedCatName = cat.name.trim();
          const dbCount = counts.find(c => c.name === normalizedCatName);
          return {
            ...cat,
            partnerCount: dbCount ? dbCount.count : cat.partnerCount
          };
        })
      );
    };

    fetchPartnerCounts();
  }, []);

  return (
    <section className="py-8 lg:py-16 bg-primary border-t border-accent/20">
      <div className="container mx-auto px-4 lg:px-6">
        <div className="text-center mb-6 lg:mb-12">
          <h2 className="text-2xl lg:text-4xl font-bold text-white font-amiri mb-2 lg:mb-4">
            {t('فئات الشركاء', 'Partner Categories')}
          </h2>
          <p className="text-sm lg:text-lg text-white/70">
            {t('اكتشف خصومات حصرية في جميع الفئات', 'Discover exclusive discounts across all categories')}
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-3 lg:gap-6">
          {categories.map((category) => (
            <Link
              key={category.id}
              href="/partners-network"
              className="group bg-white/10 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden transition-premium hover:shadow-xl hover:-translate-y-2 border border-accent/20 hover:border-accent/50"
            >
              <div className={`h-20 lg:h-32 bg-gradient-to-br ${category.color} relative overflow-hidden`}>
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  {category.svgLogo ? (
                    <>
                      <Image
                        src={category.svgLogo}
                        alt={t(category.name, category.nameEn)}
                        width={40}
                        height={40}
                        className="lg:hidden opacity-90"
                      />
                      <Image
                        src={category.svgLogo}
                        alt={t(category.name, category.nameEn)}
                        width={64}
                        height={64}
                        className="hidden lg:block opacity-90"
                      />
                    </>
                  ) : (
                    <>
                      <Icon name={category.icon as any} size={40} className="lg:hidden text-white opacity-90" />
                      <Icon name={category.icon as any} size={64} className="hidden lg:block text-white opacity-90" />
                    </>
                  )}
                </div>
              </div>

              <div className="p-3 lg:p-6">
                <h3 className="text-base lg:text-2xl font-bold text-white font-amiri mb-1 lg:mb-2">
                  {t(category.name, category.nameEn)}
                </h3>
                <p className="text-xs lg:text-sm text-white/60 mb-2 lg:mb-4">
                  {t(category.description, category.descriptionEn)}
                </p>
                <div className="flex items-center justify-between">
                  <Icon
                    name="ArrowLeftIcon"
                    size={16}
                    className="text-accent transition-premium group-hover:translate-x-1"
                  />
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center mt-6 lg:mt-12">
          <Link
            href="/partners-network"
            className="inline-flex items-center gap-2 px-6 py-3 lg:px-8 lg:py-4 bg-accent text-white rounded-xl font-bold text-sm lg:text-lg transition-premium hover:bg-accent/90 hover:shadow-lg active-press"
          >
            <Icon name="BuildingStorefrontIcon" size={20} />
            <span>{t('عرض جميع الشركاء', 'View All Partners')}</span>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CategoriesSection;